/**
 * Shared Sentry configuration constants and utilities.
 * Centralizes common configuration to reduce duplication.
 */

// Re-export static asset utilities from shared module for backward compatibility
export {
  STATIC_FILES as SENTRY_STATIC_FILES,
  STATIC_EXTENSIONS as SENTRY_STATIC_EXTENSIONS,
  isStaticAsset,
} from '@/lib/utils/static-assets';

/**
 * Log levels to capture via console logging integration.
 * Only captures info, warn, and error to reduce noise and costs.
 */
export const SENTRY_CONSOLE_LOG_LEVELS: ('info' | 'warn' | 'error')[] = ['info', 'warn', 'error'];

/**
 * Common Sentry configuration options shared across all runtimes.
 */
export const SENTRY_COMMON_CONFIG = {
  enableLogs: true,
  enableMetrics: true,
  sendDefaultPii: true, // Send user IP, user agent, etc. for better debugging
  consoleLoggingIntegration: {
    levels: SENTRY_CONSOLE_LOG_LEVELS,
  },
} as const;

/**
 * Traces sample rate configuration.
 * Uses 10% sampling in production to reduce overhead and costs (100% in development for debugging).
 * AI agent monitoring works independently via vercelAIIntegration and experimental_telemetry.
 */
export function getTracesSampleRate(): number {
  return process.env.NODE_ENV === 'production' ? 0.1 : 1.0;
}

/**
 * Get the release identifier for Sentry release tracking.
 * Uses VERCEL_GIT_COMMIT_SHA in Vercel deployments (automatically set).
 * Falls back to git commit SHA in local development (optional, Node.js runtime only).
 *
 * @returns Release identifier (git commit SHA) or undefined if not available
 */
export function getSentryRelease(): string | undefined {
  // In Vercel, use the automatically set VERCEL_GIT_COMMIT_SHA
  // This works for both Node.js and Edge runtimes
  if (process.env.VERCEL_GIT_COMMIT_SHA) {
    return process.env.VERCEL_GIT_COMMIT_SHA;
  }

  // Also check for NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA as a fallback
  if (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA) {
    return process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA;
  }

  // In local development, try to get git commit SHA (optional, Node.js runtime only)
  // Edge runtime doesn't have access to child_process, so skip git command there
  if (process.env.NODE_ENV === 'development' && process.env.NEXT_RUNTIME !== 'edge') {
    try {
      // Use child_process to get git commit SHA synchronously
      // This is safe because it's only called during initialization in Node.js runtime
      // Use require() for synchronous Node.js built-in module access
      // Note: In ESM, we could use import, but execSync needs synchronous access
      const { execSync } = require('node:child_process');
      const commitSha = execSync('git rev-parse HEAD', { encoding: 'utf-8', stdio: 'pipe' }).trim();
      return commitSha || undefined;
    } catch {
      // Silently fail if git is not available or command fails
      return undefined;
    }
  }

  return undefined;
}
